import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { MusicControlData } from "../../types/websocket";
import { getTheme } from "../../config/sessionThemes";

/**
 * Preset ambient tracks.
 * Place matching MP3 files in /public/audio/ or replace URLs with any
 * direct-link audio file URL (MP3, OGG, WAV …).
 */
const PRESET_TRACKS = [
  { title: "Tavern Ambience", url: "/audio/tavern.mp3" },
  { title: "Forest Ambience", url: "/audio/forest.mp3" },
  { title: "Battle Music", url: "/audio/battle.mp3" },
  { title: "Dungeon Ambience", url: "/audio/dungeon.mp3" },
  { title: "Town Music", url: "/audio/town.mp3" },
  { title: "Epic Boss Battle", url: "/audio/boss.mp3" },
];

interface MusicPanelProps {
  sessionId: string;
  isDM: boolean;
  theme?: string;
  className?: string;
}

interface CurrentTrack {
  url: string;
  title: string;
}

export const MusicPanel: React.FC<MusicPanelProps> = ({
  sessionId,
  isDM,
  theme = "dark",
  className = "",
}) => {
  const currentTheme = getTheme(theme);
  const { sendMessage, lastMessage } = useWebSocketContext();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [trackUrl, setTrackUrl] = useState("");
  const [trackTitle, setTrackTitle] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [currentTrack, setCurrentTrack] = useState<CurrentTrack | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  // ── React to incoming music_control events ───────────────────────────────

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type !== "music_control") return;
    if (String(lastMessage.sessionId) !== String(sessionId)) return;

    const data = lastMessage.data as MusicControlData;

    switch (data.action) {
      case "play": {
        const track: CurrentTrack = {
          url: data.trackUrl ?? "",
          title: data.trackTitle ?? "Shared Track",
        };
        setCurrentTrack(track);
        setIsPlaying(true);
        setAudioError(null);
        if (audioRef.current) {
          audioRef.current.src = track.url;
          audioRef.current.volume = volume;
          if (data.position != null) audioRef.current.currentTime = data.position;
          audioRef.current
            .play()
            .catch((e: Error) => setAudioError(`Playback error: ${e.message}`));
        }
        break;
      }
      case "pause":
        setIsPlaying(false);
        audioRef.current?.pause();
        break;
      case "stop":
        setIsPlaying(false);
        setCurrentTrack(null);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
        }
        break;
      case "seek":
        if (data.position != null && audioRef.current) {
          audioRef.current.currentTime = data.position;
        }
        break;
      case "volume":
        if (data.volume != null) {
          setVolume(data.volume);
          if (audioRef.current) audioRef.current.volume = data.volume;
        }
        break;
    }
  }, [lastMessage, sessionId, volume]);

  // ── DM controls ──────────────────────────────────────────────────────────

  const handlePlay = useCallback(() => {
    const url = trackUrl.trim();
    if (!url) return;
    const title = trackTitle.trim() || "Shared Track";

    sendMessage(
      "music_control",
      { action: "play", trackUrl: url, trackTitle: title, position: 0 } satisfies MusicControlData,
      undefined,
      sessionId
    );
    setCurrentTrack({ url, title });
    setIsPlaying(true);
    setAudioError(null);
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.volume = volume;
      audioRef.current.play().catch((e: Error) => setAudioError(`Playback error: ${e.message}`));
    }
  }, [trackUrl, trackTitle, sendMessage, sessionId, volume]);

  const handlePause = useCallback(() => {
    sendMessage(
      "music_control",
      {
        action: "pause",
        position: audioRef.current?.currentTime ?? 0,
      } satisfies MusicControlData,
      undefined,
      sessionId
    );
    setIsPlaying(false);
    audioRef.current?.pause();
  }, [sendMessage, sessionId]);

  const handleStop = useCallback(() => {
    sendMessage(
      "music_control",
      { action: "stop" } satisfies MusicControlData,
      undefined,
      sessionId
    );
    setIsPlaying(false);
    setCurrentTrack(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
  }, [sendMessage, sessionId]);

  const handleVolumeChange = useCallback(
    (newVol: number) => {
      setVolume(newVol);
      if (audioRef.current) audioRef.current.volume = newVol;
      if (isDM) {
        sendMessage(
          "music_control",
          { action: "volume", volume: newVol } satisfies MusicControlData,
          undefined,
          sessionId
        );
      }
    },
    [isDM, sendMessage, sessionId]
  );

  const selectPreset = useCallback((preset: (typeof PRESET_TRACKS)[number]) => {
    setTrackUrl(preset.url);
    setTrackTitle(preset.title);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={`flex flex-col h-full overflow-hidden ${className}`}
      style={{
        backgroundColor: currentTheme.colors.panelBg,
        color: currentTheme.colors.textPrimary,
      }}
      data-testid="music-panel"
    >
      {/* Hidden audio element — used by both DM and players */}
      <audio
        ref={audioRef}
        style={{ display: "none" }}
        onEnded={() => setIsPlaying(false)}
        onError={() => setAudioError("Failed to load audio. Check the URL.")}
      />

      {/* Panel header */}
      <div
        className="flex items-center gap-2 px-3 py-2 flex-shrink-0 border-b"
        style={{
          backgroundColor: currentTheme.colors.panelHeader,
          borderColor: currentTheme.colors.sidebarBorder,
        }}
      >
        <span className="text-base" aria-hidden="true">
          🎵
        </span>
        <span className="text-sm font-semibold">{isDM ? "Music — DM Controls" : "Music"}</span>
        {currentTrack && (
          <span
            className="ml-auto text-xs truncate max-w-[160px]"
            style={{ color: currentTheme.colors.textMuted }}
            title={currentTrack.title}
          >
            {isPlaying ? "▶ " : "⏸ "}
            {currentTrack.title}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 codex-scroll">
        {isDM ? (
          <>
            {/* Track URL */}
            <div>
              <label
                className="block text-xs mb-1"
                style={{ color: currentTheme.colors.textMuted }}
                htmlFor="music-url"
              >
                Audio File URL
              </label>
              <input
                id="music-url"
                type="url"
                value={trackUrl}
                onChange={(e) => setTrackUrl(e.target.value)}
                placeholder="https://example.com/ambient.mp3"
                className="w-full px-2 py-1.5 text-xs rounded border focus:outline-none"
                style={{
                  backgroundColor: currentTheme.colors.surface,
                  borderColor: currentTheme.colors.sidebarBorder,
                  color: currentTheme.colors.textPrimary,
                }}
              />
            </div>

            {/* Track title */}
            <div>
              <label
                className="block text-xs mb-1"
                style={{ color: currentTheme.colors.textMuted }}
                htmlFor="music-title"
              >
                Track Title (optional)
              </label>
              <input
                id="music-title"
                type="text"
                value={trackTitle}
                onChange={(e) => setTrackTitle(e.target.value)}
                placeholder="Tavern Ambience…"
                className="w-full px-2 py-1.5 text-xs rounded border focus:outline-none"
                style={{
                  backgroundColor: currentTheme.colors.surface,
                  borderColor: currentTheme.colors.sidebarBorder,
                  color: currentTheme.colors.textPrimary,
                }}
              />
            </div>

            {/* Preset tracks */}
            <div>
              <div className="text-xs mb-1" style={{ color: currentTheme.colors.textMuted }}>
                Presets
              </div>
              <div className="grid grid-cols-2 gap-1">
                {PRESET_TRACKS.map((p) => (
                  <button
                    key={p.url}
                    onClick={() => selectPreset(p)}
                    className="text-xs px-2 py-1 rounded truncate text-left hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: currentTheme.colors.surface,
                      color: currentTheme.colors.textSecondary,
                    }}
                    title={p.title}
                  >
                    {p.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Playback controls */}
            <div className="flex items-center gap-2">
              {!isPlaying ? (
                <button
                  onClick={handlePlay}
                  disabled={!trackUrl.trim()}
                  className="flex-1 py-1.5 text-xs rounded font-medium disabled:opacity-40 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: currentTheme.colors.accent, color: "#fff" }}
                  data-testid="music-play-btn"
                >
                  ▶ Play &amp; Share
                </button>
              ) : (
                <>
                  <button
                    onClick={handlePause}
                    className="flex-1 py-1.5 text-xs rounded font-medium hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: currentTheme.colors.warning, color: "#fff" }}
                    data-testid="music-pause-btn"
                  >
                    ⏸ Pause
                  </button>
                  <button
                    onClick={handleStop}
                    className="flex-1 py-1.5 text-xs rounded font-medium hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: currentTheme.colors.error, color: "#fff" }}
                    data-testid="music-stop-btn"
                  >
                    ⏹ Stop
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          /* ── Player view ──────────────────────────────────────────────────── */
          <div className="flex flex-col items-center justify-center h-24 text-center gap-1">
            {currentTrack ? (
              <>
                <div className="text-3xl">{isPlaying ? "🎵" : "⏸"}</div>
                <div className="text-sm font-medium">{currentTrack.title}</div>
                <div className="text-xs" style={{ color: currentTheme.colors.textMuted }}>
                  {isPlaying ? "Now Playing" : "Paused by DM"}
                </div>
              </>
            ) : (
              <>
                <div className="text-3xl opacity-30">🔇</div>
                <div className="text-xs" style={{ color: currentTheme.colors.textMuted }}>
                  No music playing
                </div>
              </>
            )}
          </div>
        )}

        {/* Volume (visible to everyone) */}
        <div className="flex items-center gap-2">
          <span className="text-xs" aria-hidden="true">
            🔊
          </span>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(volume * 100)}
            onChange={(e) => handleVolumeChange(Number(e.target.value) / 100)}
            className="flex-1 h-1 rounded cursor-pointer accent-current"
            aria-label="Music volume"
          />
          <span
            className="text-xs w-8 text-right tabular-nums"
            style={{ color: currentTheme.colors.textMuted }}
          >
            {Math.round(volume * 100)}%
          </span>
        </div>

        {/* Error feedback */}
        {audioError && (
          <div
            className="text-xs rounded px-2 py-1"
            style={{
              backgroundColor: currentTheme.colors.error + "33",
              color: currentTheme.colors.error,
            }}
          >
            {audioError}
          </div>
        )}
      </div>
    </div>
  );
};
